import React from 'react';
import { Link } from 'react-router-dom';
import { Twitter, Send, Instagram, Facebook, Mail, MapPin } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { mockData } from '../mock/data';
import { COMPANY } from '../lib/company';
import { openCookieSettings } from '../lib/consent';
import { usePillarPages } from '../lib/hub';

const Footer = () => {
  const { t } = useTranslation();
  const { items: pillars } = usePillarPages();

  const useCaseLinks = mockData.useCases.map((useCase) => ({
    name: t(`useCasePage.useCases.${useCase.slug}.title`) || useCase.title,
    href: `/use-cases/${useCase.slug}`
  }));

  const footerSections = [
    {
      title: t('footer.products.title'),
      links: t('footer.products.items')
        .filter((item, index) => index !== 2) // Remove "API Access" (3rd item, index 2)
        .map((item, index) => ({
          name: item,
          href: index === 0 ? '/pricing/residential' : '/pricing/mobile'
        }))
    },
    {
      title: t('footer.useCases.title'),
      links: useCaseLinks
    },
    {
      title: t('footer.company.title'),
      links: [
        { name: t('footer.company.items')[0], href: '/about' },
        { name: t('footer.company.items')[1], href: '/careers' },
        { name: t('footer.blog'), href: '/blog' },
        { name: t('seo.integrations.title'), href: '/integrations' },
        { name: t('footer.support.items')[0], href: '/contact' },
      ]
    },
    ...(pillars.length
      ? [{ title: t('footer.guides'), links: pillars.map((doc) => ({ name: doc.title, href: doc.path })) }]
      : []),
    {
      title: t('footer.legal.title'),
      links: [
        { name: t('footer.legal.legalNotice'), href: '/legal' },
        { name: t('footer.legal.terms'), href: '/terms-of-service' },
        { name: t('footer.legal.privacy'), href: '/privacy-policy' },
        { name: t('footer.legal.cookies'), href: '/cookie-policy' },
        { name: t('footer.legal.refund'), href: '/refund-policy' },
        { name: t('footer.legal.aup'), href: '/acceptable-use-policy' },
      ]
    }
  ];

  return (
    <footer className="relative bg-slate-950 border-t border-slate-800 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
        <div className={`grid grid-cols-2 md:grid-cols-4 ${pillars.length ? 'lg:grid-cols-7' : 'lg:grid-cols-6'} gap-10`}>
          <div className="col-span-2">
            <Link to="/" className="inline-flex items-center mb-5">
              <img
                src="/logo-white.png"
                alt="IP9"
                className="h-8 w-auto"
              />
            </Link>
            <p className="text-slate-400 text-sm mb-6 max-w-sm leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="space-y-3 text-sm text-slate-400">
              <a href={`mailto:${COMPANY.emails.support}`} className="flex items-center gap-3 hover:text-white transition-colors">
                <Mail className="w-4 h-4 text-cyan-500" />
                <span>{COMPANY.emails.support}</span>
              </a>
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-cyan-500 shrink-0" />
                <span>{COMPANY.address}</span>
              </div>
            </div>
            <div className="mt-7">
              <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-3">{t('footer.weAccept')}</p>
              <ul className="flex items-center gap-2">
                {[
                  { src: '/payments/visa.png', alt: 'Visa', className: 'h-3.5' },
                  { src: '/payments/mastercard.png', alt: 'Mastercard', className: 'h-6' },
                ].map((card) => (
                  <li key={card.alt} className="flex items-center justify-center h-9 w-14 bg-white border border-slate-700">
                    <img src={card.src} alt={card.alt} className={`${card.className} w-auto`} loading="lazy" />
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {footerSections.map((section) => (
            <div key={section.title}>
              <h3 className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500 mb-4">{section.title}</h3>
              <ul className="space-y-2.5">
                {section.links.map((link) => (
                  <li key={link.href}>
                    <Link to={link.href} className="text-slate-300 hover:text-cyan-400 text-sm transition-colors duration-200">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between gap-6">
          <p className="text-xs text-slate-500 leading-relaxed max-w-3xl">
            {COMPANY.tradingName} {t('footer.companyLine.tradingNameOf')} {COMPANY.legalName}, {t('footer.companyLine.registeredIn')} ({t('footer.companyLine.companyNo')} {COMPANY.companyNumber}).{' '}
            {t('footer.companyLine.registeredOffice')}: {COMPANY.address}.
          </p>
          <div className="flex items-center gap-5 shrink-0">
            <button type="button" onClick={openCookieSettings} className="text-xs text-slate-400 hover:text-white underline underline-offset-2">
              {t('footer.cookieSettings')}
            </button>
            <a href="https://x.com/IP9Solutions" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors" title="Follow us on X">
              <Twitter className="w-5 h-5" />
            </a>
            <a href="https://t.me/ip9solutions" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors" title="Join our Telegram">
              <Send className="w-5 h-5" />
            </a>
            <a href="https://www.instagram.com/ip9proxy" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors" title="Follow us on Instagram">
              <Instagram className="w-5 h-5" />
            </a>
            <a href="https://www.facebook.com/IP9Proxies/" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors" title="Follow us on Facebook">
              <Facebook className="w-5 h-5" />
            </a>
          </div>
        </div>

        <div className="mt-6 text-xs text-slate-600 font-mono">
          © {new Date().getFullYear()} {COMPANY.legalName}. {t('footer.rightsReserved')}
        </div>
      </div>
      <div aria-hidden="true" className="pointer-events-none select-none font-display font-bold text-outline text-[22vw] leading-[0.8] -mb-[4vw] text-center tracking-tighter">
        IP9
      </div>
    </footer>
  );
};

export default Footer;
